import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { Trophy, Target, ChartBar, TennisBall, Sword, ArrowLeft, Medal, Broadcast, Watch, ShareNetwork, Brain, PuzzlePiece, Calendar, Buildings, HandFist, MapPin } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import UserAvatar from "@/components/UserAvatar";
import DydProfileBlock from "@/components/DydProfileBlock";
import PublicAvatar3D from "@/components/PublicAvatar3D";
import QuickReportButton from "@/components/QuickReportButton";
import BestPairs from "@/components/BestPairs";
import { useStatsBus } from "@/lib/statsBus";

const DEVICE_LABEL = { apple: "Apple Watch", suunto: "Suunto" };
const POSITION_LABEL = { drive: "Drive (derecha)", reves: "Revés (izquierda)", ambos: "Ambos" };
const HAND_LABEL = { diestro: "Diestro", zurdo: "Zurdo" };
const DAY_LABEL = { lunes: "Lun", miercoles: "Mié", viernes: "Vie", domingo: "Dom" };

export default function PlayerProfile() {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [overview, setOverview] = useState(null);
  const [history, setHistory] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  useStatsBus(() => setRefreshTick((t) => t + 1));

  useEffect(() => {
    Promise.all([
      api.get(`/users/${userId}/profile`),
      api.get(`/player/${userId}/overview`),
      api.get(`/statistics/player/${userId}/history`),
      api.get(`/matches?user_id=${userId}&status=validado&limit=30`),
    ]).then(([p, o, h, m]) => {
      setProfile(p.data);
      setOverview(o.data);
      setHistory(h.data);
      setMatches(m.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [userId, refreshTick]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;
  if (!profile) return <div className="text-gray-400 text-center py-12">Jugador no encontrado</div>;

  const stats = profile.stats || {};
  const winRate = stats.games_played > 0 ? Math.round((stats.games_won / stats.games_played) * 100) : 0;

  const chartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[#1A1A1A] border border-white/10 p-3 text-xs">
        <p className="text-gray-400 mb-1">{label}</p>
        {payload.map((p, i) => <p key={i} style={{ color: p.color }} className="font-semibold">{p.name}: {p.value}</p>)}
      </div>
    );
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="player-profile-page">
      <Link to="/dashboard/estadisticas" className="inline-flex items-center text-gray-400 hover:text-[#CCFF00] text-sm transition-colors" data-testid="back-to-stats">
        <ArrowLeft size={16} className="mr-1" /> Volver
      </Link>

      <DydProfileBlock userId={userId} />

      {/* Public 3D Avatar with brand logos legibly rendered */}
      <PublicAvatar3D userId={userId} height={440} testId="player-profile-avatar-3d" />

      {/* Best duplas by rolling period (week/month/year/all) */}
      <BestPairs userId={userId} userName={profile.name} defaultPeriod="week" testid="profile-best-pairs" />

      {/* Player Card */}
      <div className="card-tactical p-8" data-testid="player-info-card">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <UserAvatar user={overview?.user || profile} size={88} className="border-2 border-[#CCFF00] shrink-0" testId="player-profile-avatar" topBadges={(overview?.badges || []).filter(b => b.earned).slice(0, 5)} />
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">{profile.role}</p>
              <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">{profile.name}</h1>
              <div className="flex gap-3 mt-2 text-sm text-gray-400 flex-wrap items-center">
                {profile.email && <span>{profile.email}</span>}
                {profile.phone && <span>{profile.phone}</span>}
                {Object.keys(profile.linked_devices || {}).map((k) => (
                  <Badge key={k} variant="outline" className="bg-blue-500/10 text-blue-300 border-blue-500/30 text-[10px] uppercase tracking-wider" data-testid={`device-badge-${k}`}>
                    <Watch size={12} className="inline mr-1" /> {DEVICE_LABEL[k] || k}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-6 items-center">
            <div className="text-center">
              <span className="font-['Barlow_Condensed'] text-4xl font-black text-[#CCFF00]">#{stats.ranking || "-"}</span>
              <p className="text-xs uppercase tracking-wider text-gray-500 mt-1">Ranking</p>
            </div>
            <div className="text-center">
              <span className="font-['Barlow_Condensed'] text-4xl font-black text-white">{winRate}%</span>
              <p className="text-xs uppercase tracking-wider text-gray-500 mt-1">Victoria</p>
            </div>
            <QuickReportButton section="players" params={{ user_id: profile.id }} variant="compact" />
          </div>
        </div>
      </div>

      {/* Overview: gamified + personal + participation */}
      {overview && (
        <div className="space-y-4" data-testid="player-overview">
          {/* Row 1: Gamified + Personal */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="card-tactical p-4" data-testid="ov-trivia">
              <Brain size={18} weight="duotone" className="text-purple-400 mb-1" />
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-500">Trivia</p>
              <p className="font-['Barlow_Condensed'] text-2xl font-black text-white leading-none">{overview.trivia?.best_score || 0}%</p>
              <p className="text-[10px] text-gray-500 mt-1">Prom: {overview.trivia?.avg_score || 0}% · {overview.trivia?.total_played || 0} pj</p>
            </div>
            <div className="card-tactical p-4" data-testid="ov-memorama">
              <PuzzlePiece size={18} weight="duotone" className="text-pink-400 mb-1" />
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-500">Memorama</p>
              <p className="font-['Barlow_Condensed'] text-2xl font-black text-white leading-none">{overview.memorama?.best_score || 0}%</p>
              <p className="text-[10px] text-gray-500 mt-1">Min mov: {overview.memorama?.fewest_moves || "—"} · {overview.memorama?.total_played || 0} pj</p>
            </div>
            <div className="card-tactical p-4" data-testid="ov-tournaments">
              <Trophy size={18} weight="duotone" className="text-yellow-400 mb-1" />
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-500">Torneos</p>
              <p className="font-['Barlow_Condensed'] text-2xl font-black text-white leading-none">{overview.tournaments?.played || 0}</p>
              <p className="text-[10px] text-gray-500 mt-1">{overview.tournaments?.by_matches?.wins || 0}V · {overview.tournaments?.by_matches?.losses || 0}D · {overview.tournaments?.by_matches?.points || 0}pts</p>
            </div>
            <div className="card-tactical p-4" data-testid="ov-leagues">
              <Medal size={18} weight="duotone" className="text-amber-400 mb-1" />
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-500">Ligas</p>
              <p className="font-['Barlow_Condensed'] text-2xl font-black text-white leading-none">{overview.leagues?.length || 0}</p>
              <p className="text-[10px] text-gray-500 mt-1 truncate">{(overview.leagues || []).map(l => l.name).join(", ") || "Ninguna"}</p>
            </div>
            <div className="card-tactical p-4" data-testid="ov-retas">
              <Sword size={18} weight="duotone" className="text-cyan-400 mb-1" />
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-500">Retas</p>
              <p className="font-['Barlow_Condensed'] text-2xl font-black text-white leading-none">{overview.retas?.multi_participations || 0}</p>
              <p className="text-[10px] text-gray-500 mt-1">{overview.retas?.by_matches?.wins || 0}V · {overview.retas?.by_matches?.losses || 0}D · {overview.retas?.by_matches?.points || 0}pts</p>
            </div>
            <div className="card-tactical p-4" data-testid="ov-rentals">
              <Buildings size={18} weight="duotone" className="text-orange-400 mb-1" />
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-500">Rentas</p>
              <p className="font-['Barlow_Condensed'] text-2xl font-black text-white leading-none">{overview.rentals?.by_matches?.matches || 0}</p>
              <p className="text-[10px] text-gray-500 mt-1">{overview.rentals?.by_matches?.wins || 0}V · {overview.rentals?.by_matches?.losses || 0}D · {overview.rentals?.by_matches?.points || 0}pts</p>
            </div>
          </div>

          {/* Row 2: Position / Hand / Category / Experience */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="card-tactical p-4" data-testid="ov-position">
              <MapPin size={16} weight="duotone" className="text-[#CCFF00] mb-1" />
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-500">Posición</p>
              <p className="text-sm text-white font-semibold mt-1">{POSITION_LABEL[overview.user?.position] || "—"}</p>
            </div>
            <div className="card-tactical p-4" data-testid="ov-hand">
              <HandFist size={16} weight="duotone" className="text-[#CCFF00] mb-1" />
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-500">Mano</p>
              <p className="text-sm text-white font-semibold mt-1">{HAND_LABEL[overview.user?.dominant_hand] || "—"}</p>
            </div>
            <div className="card-tactical p-4" data-testid="ov-category">
              <Target size={16} weight="duotone" className="text-[#CCFF00] mb-1" />
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-500">Categoría</p>
              <p className="text-sm text-white font-semibold mt-1">{overview.user?.category ? `Cat. ${overview.user.category}` : "—"}</p>
            </div>
            <div className="card-tactical p-4" data-testid="ov-experience">
              <ChartBar size={16} weight="duotone" className="text-[#CCFF00] mb-1" />
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-500">Experiencia</p>
              <p className="text-sm text-white font-semibold mt-1">{overview.user?.experience_years ? `${overview.user.experience_years} años` : "—"}</p>
            </div>
          </div>

          {/* Row 3: Retas por día */}
          <div className="card-tactical p-4" data-testid="ov-retas-by-day">
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={16} weight="duotone" className="text-cyan-400" />
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00]">Retas por día</p>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {["lunes", "miercoles", "viernes", "domingo"].map((d) => {
                const r = overview.retas_by_day?.[d] || {};
                return (
                  <div key={d} className="bg-[#0A0A0A] border border-white/5 p-3" data-testid={`ov-reta-day-${d}`}>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500">{DAY_LABEL[d]}</p>
                    <p className="font-['Barlow_Condensed'] text-xl font-black text-white leading-none mt-1">{r.points || 0} <span className="text-xs text-gray-500 font-normal">pts</span></p>
                    <p className="text-[10px] text-gray-500 mt-1">{r.wins || 0}V · {r.losses || 0}D · {r.matches || 0}pj</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Medallas / Badges */}
      {overview?.badges && overview.badges.length > 0 && (
        <div className="card-tactical p-5" data-testid="player-badges">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Medal size={18} weight="duotone" className="text-[#CCFF00]" />
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00]">Medallas por hitos</p>
            </div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500" data-testid="badges-count">
              <span className="text-[#CCFF00] font-bold">{overview.badges_earned_count || 0}</span> / {overview.badges_total || 0} desbloqueadas
            </p>
          </div>
          {(() => {
            const groups = overview.badges.reduce((acc, b) => { (acc[b.group] = acc[b.group] || []).push(b); return acc; }, {});
            const groupOrder = ["partidos", "victorias", "puntos", "torneos", "retas", "ligas", "trivia"];
            const groupLabels = { partidos: "Partidos", victorias: "Victorias", puntos: "Puntos", torneos: "Torneos", retas: "Retas", ligas: "Ligas", trivia: "Trivia y Juegos" };
            return groupOrder.filter(g => groups[g]).map((g) => (
              <div key={g} className="mb-4 last:mb-0" data-testid={`badge-group-${g}`}>
                <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 font-bold">{groupLabels[g]}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                  {groups[g].map((b) => (
                    <div
                      key={b.code}
                      className={`relative border p-3 transition-all ${b.earned ? "bg-[#CCFF00]/5 border-[#CCFF00]/30" : "bg-[#0A0A0A] border-white/5 opacity-60"}`}
                      title={b.description}
                      data-testid={`badge-${b.code}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-2xl ${b.earned ? "" : "grayscale"}`}>{b.icon}</span>
                        <p className={`text-[11px] font-bold leading-tight ${b.earned ? "text-[#CCFF00]" : "text-gray-400"}`}>{b.name}</p>
                      </div>
                      <p className="text-[9px] text-gray-500 leading-tight mb-2 line-clamp-2">{b.description}</p>
                      {!b.earned && (
                        <>
                          <div className="h-1 bg-white/5 overflow-hidden">
                            <div className="h-full bg-[#CCFF00]/40" style={{ width: `${Math.min(100, b.progress_pct || 0)}%` }} />
                          </div>
                          <p className="text-[9px] text-gray-600 mt-1 font-['JetBrains_Mono']">{b.progress}/{b.threshold}</p>
                        </>
                      )}
                      {b.earned && b.earned_at && (
                        <p className="text-[9px] text-[#CCFF00]/70 font-['JetBrains_Mono']">✓ {b.earned_at.slice(0, 10)}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="player-stats-grid">
        {[
          { label: "Puntos", value: stats.points || 0, icon: Target, color: "#CCFF00" },
          { label: "Partidos", value: stats.games_played || 0, icon: ChartBar, color: "#007AFF" },
          { label: "Victorias", value: stats.games_won || 0, icon: Trophy, color: "#FFB800" },
          { label: "Bolas", value: stats.balls_hit || 0, icon: TennisBall, color: "#34C759" },
        ].map((s) => (
          <div key={s.label} className="card-tactical p-5">
            <s.icon size={20} weight="duotone" style={{ color: s.color }} className="mb-2" />
            <p className="font-['Barlow_Condensed'] text-3xl font-black text-white">{s.value}</p>
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      {history.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card-tactical p-6" data-testid="points-chart">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Tendencia</p>
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight mb-4">Puntos Acumulados</h3>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={history}>
                <defs><linearGradient id="colorPts" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#CCFF00" stopOpacity={0.3}/><stop offset="95%" stopColor="#CCFF00" stopOpacity={0}/></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#737373" }} />
                <YAxis tick={{ fontSize: 10, fill: "#737373" }} />
                <Tooltip content={chartTooltip} />
                <Area type="monotone" dataKey="points" name="Puntos" stroke="#CCFF00" fill="url(#colorPts)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="card-tactical p-6" data-testid="winrate-chart">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Rendimiento</p>
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight mb-4">Victorias vs Derrotas</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={history}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#737373" }} />
                <YAxis tick={{ fontSize: 10, fill: "#737373" }} />
                <Tooltip content={chartTooltip} />
                <Bar dataKey="wins" name="Victorias" fill="#CCFF00" />
                <Bar dataKey="losses" name="Derrotas" fill="#FF3B30" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tournaments */}
      {profile.tournaments?.length > 0 && (
        <div className="card-tactical p-6" data-testid="player-tournaments">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500 mb-1">Participacion</p>
          <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight mb-4">Torneos</h3>
          <div className="flex flex-wrap gap-2">
            {profile.tournaments.map((t) => (
              <div key={t.id} className="bg-[#0A0A0A] border border-white/10 px-4 py-2">
                <p className="text-sm font-semibold text-white">{t.name}</p>
                <div className="text-xs text-gray-500 flex items-center gap-2">{t.start_date} <Badge variant="outline" className="badge-status bg-white/5 text-gray-400 border-white/10">{t.status}</Badge></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Validated Scoreboard Matches */}
      <div className="card-tactical p-6" data-testid="validated-matches">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2"><Broadcast size={14} /> Marcador</p>
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight">Partidos Validados</h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 font-['JetBrains_Mono']">{matches.length} partidos</span>
            {matches.length >= 5 && (
              <Button size="sm" onClick={async () => {
                const url = `${window.location.origin}/p/${userId}`;
                const text = `${profile.name} · DyD Padel`;
                if (navigator.share) { try { await navigator.share({ title: text, url }); } catch { /* user dismissed */ } }
                else { try { await navigator.clipboard.writeText(url); toast.success("Enlace publico copiado"); } catch { toast.error("No se pudo copiar"); } }
              }} className="bg-[#CCFF00]/10 text-[#CCFF00] hover:bg-[#CCFF00]/20 border border-[#CCFF00]/30 uppercase text-xs font-bold" data-testid="share-profile-btn">
                <ShareNetwork size={14} className="mr-1" /> Compartir perfil
              </Button>
            )}
          </div>
        </div>
        {matches.length === 0 ? (
          <p className="text-gray-600 text-sm">Aun no hay partidos validados en el marcador</p>
        ) : (
          <div className="space-y-2">
            {matches.map((m) => {
              const inPair1 = [m.pair1?.p1_id, m.pair1?.p2_id].includes(userId);
              const myPair = inPair1 ? 1 : 2;
              const won = m.winner_pair === myPair;
              const s1 = m.sets.filter((s) => s.winner === 1).length;
              const s2 = m.sets.filter((s) => s.winner === 2).length;
              const scoreStr = m.sets.map((s) => `${s.pair1_games}-${s.pair2_games}${s.tiebreak ? ` (${s.tiebreak.pair1}-${s.tiebreak.pair2})` : ""}`).join(" / ");
              const partnerName = inPair1 ? (m.pair1?.p1_id === userId ? m.pair1?.p2_name : m.pair1?.p1_name) : (m.pair2?.p1_id === userId ? m.pair2?.p2_name : m.pair2?.p1_name);
              const rivals = inPair1 ? `${m.pair2?.p1_name} / ${m.pair2?.p2_name}` : `${m.pair1?.p1_name} / ${m.pair1?.p2_name}`;
              return (
                <Link to={`/dashboard/marcador/${m.id}`} key={m.id} className="block bg-[#0A0A0A] border border-white/10 p-4 hover:border-[#CCFF00]/40 transition-colors" data-testid={`validated-match-${m.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-widest text-gray-500">{m.date}{m.time ? ` · ${m.time}` : ""} · {m.format === "short" ? "Tabla 4-0" : "2 de 3 sets"}</p>
                      <p className="text-sm text-white font-semibold mt-1">Con {partnerName || "—"} <span className="text-gray-600">vs</span> {rivals}</p>
                      <p className="font-['JetBrains_Mono'] text-xs text-gray-400 mt-1">{scoreStr}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-['Barlow_Condensed'] font-black text-2xl text-white">{inPair1 ? `${s1}-${s2}` : `${s2}-${s1}`}</span>
                      <Badge variant="outline" className={`badge-status ${won ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}`}>
                        {won ? <><Medal size={12} className="mr-1 inline" />Victoria</> : "Derrota"}
                      </Badge>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Match History (legacy from retas) */}
      <div className="card-tactical p-6" data-testid="match-history">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500 mb-1">Historial</p>
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight mb-4">Partidos Recientes</h3>
        {profile.match_history?.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="border-white/5">
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Fecha</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Oponente</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Score</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">Resultado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {profile.match_history.map((m, i) => (
                <TableRow key={i} className="border-white/5 hover:bg-white/5" data-testid={`match-row-${i}`}>
                  <TableCell className="text-gray-400 text-sm">{m.date}</TableCell>
                  <TableCell className="text-white font-semibold text-sm flex items-center gap-2">
                    <Sword size={14} className="text-gray-600" /> {m.opponent}
                  </TableCell>
                  <TableCell className="font-['JetBrains_Mono'] text-sm text-gray-400">{m.score || "-"}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline" className={`badge-status ${m.result === "victoria" ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}`}>
                      {m.result === "victoria" ? <><Medal size={12} className="mr-1 inline" />Victoria</> : "Derrota"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-gray-600 text-sm">No hay partidos completados</p>
        )}
      </div>
    </div>
  );
}
